import { Button, useDisclosure } from "@chakra-ui/react";
import { FormikProps, Formik, useFormikContext, Form } from "formik";
import { cloneDeep, debounce } from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { BsPlay } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { CellTool } from "../../models/collaborate.model";
import { SideBarFormArguments } from "../../models/form.model";
import { NodeState, selectSelectedTool, updateRigidCellTool } from "../../store/slices/node.slice";
import { setSelectedTestTool } from "../../store/slices/tools.slice";
import { convertObjectKeyToLabel } from "../../utils/string";
import FormikFormInputControl from "../tools/FormikFormInputControl";
import ToolsLabControlPanel from "./ToolsLabControlPanel";

const AgencyToolControlPanel: React.FC = () => {
    const nodesDataState: NodeState = useSelector((state: any) => state.nodeData);
    const toolTestDisc = useDisclosure();
    const selectedToolArgumentsFormRef = useRef<FormikProps<any>>(null);
    const dispatch = useDispatch();
    const [argumentFormData, setArgumentFormData] = useState<SideBarFormArguments[]>([]);
    const selectedTool = useSelector(selectSelectedTool);
    const agencyTool =
        selectedTool?.display?.nodes?.[nodesDataState.selectedAgencyToolIndex] ||
        nodesDataState.collabApp?.json_structure.display?.tools[
            nodesDataState.selectedAgencyToolIndex
        ];

    // const selectedTool = useMemo()
    // Use useEffect to reset the form when argumentForm changes
    useEffect(() => {
        const args =
            selectedTool?.selected_tool?.arguments_object ||
            nodesDataState.cells[0].tool_wishlist[0]?.selected_tool.arguments_object;
        let formArguments: SideBarFormArguments[] = [];
        if (args) {
            // console.log("Args -> " ,args);
            const keys = Object.keys(args).filter((key) => agencyTool?.arguments.includes(key));
            /**
             * Show only arguments parameter_type equals custom or normal
             */
            formArguments = keys
                .filter((key) => ["custom", "normal", "user"].includes(args[key].parameter_type))
                .map((key) => ({
                    key,
                    description: args[key].description,
                    required: !!args[key].required,
                    label: convertObjectKeyToLabel(key),
                    type: args[key].type,
                    item_type: args[key].item_type,
                    value: args[key].default_value || "",
                    parameter_type: args[key].parameter_type,
                    value_schema: args[key].value_schema || undefined,
                    key_placeholder: args[key].key_placeholder || undefined,
                    properties: (args[key] as any).properties || undefined,
                    button_text: args[key].button_text || "",
                    depends_on: args[key].depends_on || undefined,
                    adf_structure: args[key].adf_structure || undefined,
                    textarea: args[key].textarea || false,
                    options: args[key].options || undefined,
                    items: args[key].items || undefined,
                    is_disabled: args[key].is_disabled || false,
                    title: args[key].title || undefined
                }));
        }

        const formValues = formArguments.reduce(
            (acc, obj) => ({ ...acc, [obj.key]: obj.value }),
            {}
        );

        if (selectedToolArgumentsFormRef?.current) {
            selectedToolArgumentsFormRef.current.resetForm({
                values: formValues
            });
        }

        setArgumentFormData(formArguments);
    }, [agencyTool?.arguments, nodesDataState.selectedAgencyToolIndex]);

    /**
     * Test seleced tool
     */
    const testSelectedTool = () => {
        // set selected tool wishlist as test tool
        const selectedToolWishList = selectedTool || nodesDataState.cells[0].tool_wishlist[0];

        if (selectedToolWishList) {
            dispatch(setSelectedTestTool(selectedToolWishList.selected_tool as CellTool));
            toolTestDisc.onOpen();
        }
    };

    return (
        <div
            id="tools-control-panel"
            className="relative tools-control h-full grid grid-rows-[60px_auto] overflow-hidden w-full"
        >
            <div className="header px-[16px] border-b pb-[8px] grid grid-cols-[280px_75px] items-center">
                <div className="wished-tool-details">
                    {/* <h4 className="text-[15px] font-semibold text-slate-200">
                        {agencyTool?.title || "Wished Tool Title"}
                    </h4>
                    <p className="text-[13px] w-full truncate whitespace-nowrap text-neutral-400">
                        {agencyTool?.description || "Wished Tool Description"}.
                    </p> */}
                    <h4
                        title={nodesDataState.collabApp?.title}
                        className="text-[15px] font-semibold text-slate-200 w-full overflow-hidden truncate"
                    >
                        {nodesDataState.collabApp?.title}
                    </h4>
                    <p
                        title={nodesDataState.collabApp?.description}
                        className="text-[13px] w-full truncate whitespace-nowrap text-neutral-400"
                    >
                        {nodesDataState.collabApp?.description}.
                    </p>
                </div>
                <div className="action flex justify-end">
                    <Button
                        onClick={testSelectedTool}
                        pl={"2px"}
                        py="0px"
                        pr="8px"
                        height={"33px"}
                        background={"transparent"}
                        color={"#22c55e"}
                        fontSize={"14px"}
                        className="pl-[2px] pr-[8px] py-[2px] rounded border border-green-400 hover:bg-opacity-90 text-green-500 flex"
                    >
                        <BsPlay size="22px" /> Test
                    </Button>
                </div>
            </div>
            <div className="h-full pt-6 overflow-y-auto px-[16px]">
                <Formik
                    innerRef={selectedToolArgumentsFormRef}
                    initialValues={{}}
                    // enableReinitialize={true}
                    onSubmit={() => {}}
                >
                    {(formik) => (
                        <Form>
                            {argumentFormData.map((arg, index) => (
                                <FormikFormInputControl
                                    key={`arg-control-${index}`}
                                    name={arg.key}
                                    argument={arg}
                                    formikProps={formik}
                                    showConvertToUserInputCheckbox={false}
                                    // onPersist={(checked) => persistSelectedToolArgument(arg.key, checked)}
                                />
                            ))}
                            <ToolArgFormAutoSave
                                debounceMs={500}
                                selectedTool={
                                    selectedTool?.selected_tool ||
                                    nodesDataState.cells[0].tool_wishlist[0]?.selected_tool
                                }
                            />
                        </Form>
                    )}
                </Formik>
            </div>

            {/* Wished tools output view */}
            <ToolsLabControlPanel open={toolTestDisc.isOpen} onClose={toolTestDisc.onClose} />
        </div>
    );
};

function ToolArgFormAutoSave({ debounceMs, selectedTool }) {
    const formikContext = useFormikContext();
    const dispatch = useDispatch();

    const debouncedSave = useCallback(
        debounce(async (values, tool) => {
            if (selectedTool) {
                const selectedToolCopy = cloneDeep(tool);
                // Ensure arguments_object exists
                if (!selectedToolCopy.arguments_object) {
                    selectedToolCopy.arguments_object = {};
                }
                for (const key of Object.keys(values)) {
                    // Initialize the property if it doesn't exist
                    if (!selectedToolCopy.arguments_object[key]) {
                        selectedToolCopy.arguments_object[key] = {};
                    }
                    if (values[key] === "" && !values[key]) {
                        delete selectedToolCopy.arguments_object[key].default_value;
                        continue;
                    }

                    if (selectedToolCopy.arguments_object[key].type === "number") {
                        selectedToolCopy.arguments_object[key].default_value = Number(values[key]);
                    } else if (selectedToolCopy.arguments_object[key].type === "string") {
                        selectedToolCopy.arguments_object[key].default_value = values[key].trim();
                    } else {
                        selectedToolCopy.arguments_object[key].default_value = values[key];
                    }
                }
                dispatch(
                    updateRigidCellTool({
                        selected_tool: selectedToolCopy
                    })
                );
            }
        }, debounceMs),
        []
    );

    useEffect(() => {
        debouncedSave(formikContext.values, selectedTool);
        return debouncedSave.cancel;
    }, [formikContext.values, debouncedSave]);

    return null;
}

export default AgencyToolControlPanel;
